'use client'

import React, { useEffect, useRef, useState } from 'react'
import CarouselScrollArrow from '@/components/shared/CarouselScrollArrow'
import type { ServiceItem } from '@/types/interfaces/service-item'
import ServiceTile from './ServiceTile'
import classes from './Services.module.css'

const TILE_SCROLL_STEP = 160 + 16

interface ServicesProps {
    services: ServiceItem[]
}

export default function Services({ services }: ServicesProps) {
    const scrollerRef = useRef<HTMLDivElement>(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    useEffect(() => {
        const element = scrollerRef.current
        if (!element) return

        const update = () => {
            const maxScrollLeft = element.scrollWidth - element.clientWidth
            setCanScrollLeft(element.scrollLeft > 1)
            setCanScrollRight(maxScrollLeft > 1 && element.scrollLeft < maxScrollLeft - 1)
        }

        update()
        element.addEventListener('scroll', update, { passive: true })
        const observer = new ResizeObserver(update)
        observer.observe(element)
        return () => {
            element.removeEventListener('scroll', update)
            observer.disconnect()
        }
    }, [services])

    if (services.length === 0) return null

    const scrollByStep = (direction: 'left' | 'right') => {
        const distance = direction === 'left' ? -TILE_SCROLL_STEP : TILE_SCROLL_STEP
        scrollerRef.current?.scrollBy({ left: distance, behavior: 'smooth' })
    }

    return (
        <div className={classes.frame}>
            <div ref={scrollerRef} className={classes.scroller} data-featured-category-services>
                {services.map((service) => (
                    <ServiceTile key={service.id || `${service.skuId}-${service.title}`} service={service} />
                ))}
            </div>
            {canScrollLeft && (
                <CarouselScrollArrow
                    direction="left"
                    className={`${classes.arrow} ${classes.arrowLeft}`}
                    onClick={() => scrollByStep('left')}
                />
            )}
            {canScrollRight && (
                <CarouselScrollArrow className={classes.arrow} onClick={() => scrollByStep('right')} />
            )}
        </div>
    )
}
